import React, { useState, useMemo } from 'react';
import { LayoutGrid, Lightbulb, ListFilter, FileText, MessageSquare, TrendingUp, Link } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AdvancedTable, EnhancedTableColumn } from '@/modules/app/components/ui/table';
import ExportButton from '@/components/ui/ExportButton';
import AiAnalysisButton from '@/modules/app/components/ui/AiAnalysisButton';
import { formatDistanceToNow, format, parseISO, isValid } from 'date-fns';

interface WorkspaceKeywordData {
  id: string;
  keyword?: string;
  primary_keyword: string;
  data_type: string;
  search_volume?: number;
  intent?: string;
  competition?: string;
  difficulty?: number;
  cpc?: number;
  funnel_stage?: string;
  created_at: string;
  subtopic?: string;
  people_also_ask?: string;
  answer?: string;
  url?: string;
  title?: string;
  domain?: string;
  description?: string;
  date_from?: string;
  date_to?: string;
  rank?: number; // ✅ Added rank property
}

interface WorkspaceKeywordTabsProps {
  keywordData: WorkspaceKeywordData[];
  searchFilter: string;
  isLoading?: boolean;
  workspaceName?: string;
  // AI Analysis functionality
  onAiAnalysis?: () => void;
  isAiAnalysisLoading?: boolean;
  allKeywordDataCount?: number;
}

// FIXED: Use EXACT database data types from keyword_consolidated_data table
// Updated to consolidate Related, Ideas and Suggestions into one Keywords tab
const TAB_CONFIG = [
  { id: 'keywords', label: 'Keywords', icon: LayoutGrid, dataTypes: ['related_keywords', 'keyword_ideas', 'keyword_suggestions'] },
  { id: 'subtopics', label: 'Subtopics', icon: FileText, dataType: 'subtopics' },
  { id: 'paa', label: 'People Also Ask', icon: MessageSquare, dataType: 'people_also_ask' },
  { id: 'trends', label: 'Google Trends', icon: TrendingUp, dataType: 'google_trends' },
  { id: 'serp', label: 'SERP', icon: Link, dataType: 'serp_analysis' },
];

const WorkspaceKeywordTabs: React.FC<WorkspaceKeywordTabsProps> = ({
  keywordData,
  searchFilter,
  isLoading = false,
  workspaceName,
  onAiAnalysis,
  isAiAnalysisLoading = false,
  allKeywordDataCount = 0
}) => {
  const [activeTab, setActiveTab] = useState('keywords');


  // Helper function to format dates for trends data (month-year format)
  const formatTrendsDate = (dateString?: string): string => {
    if (!dateString) return '-';
    
    try {
      // Handle both ISO strings and regular date strings
      let date: Date;
      
      // Try parsing as ISO first (handles timestamps like "2025-05-25T00:00:00+05:30")
      if (dateString.includes('T') || dateString.includes('Z')) {
        date = parseISO(dateString);
      } else {
        // Handle other date formats
        date = new Date(dateString);
      }
      
      if (isValid(date)) {
        // Get day with proper ordinal suffix (1st, 2nd, 3rd, etc.)
        const day = date.getDate();
        const ordinalSuffix = (day: number): string => {
          if (day > 3 && day < 21) return 'th';
          switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        };
        
        // Format as "1st Jan 2025"
        const dayWithSuffix = `${day}${ordinalSuffix(day)}`;
        const monthYear = format(date, 'MMM yyyy');
        return `${dayWithSuffix} ${monthYear}`;
      }
    } catch (error) {
      console.warn('Error parsing trends date:', dateString, error);
    }
    
    // Fallback: return the original string if parsing fails
    return dateString;
  };

  // Filter keyword data based on search only (AdvancedTable handles other filtering)
  const filteredKeywordData = useMemo(() => {
    return keywordData.filter(item =>
      (item.keyword?.toLowerCase() || '').includes(searchFilter.toLowerCase()) ||
      item.primary_keyword.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (item.subtopic?.toLowerCase() || '').includes(searchFilter.toLowerCase()) ||
      (item.people_also_ask?.toLowerCase() || '').includes(searchFilter.toLowerCase()) ||
      (item.title?.toLowerCase() || '').includes(searchFilter.toLowerCase())
    );
  }, [keywordData, searchFilter]);

  // Group data by type for tabs - using correct database data types
  const groupedData = useMemo(() => {
    return filteredKeywordData.reduce((acc, item) => {
      const type = item.data_type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {} as Record<string, WorkspaceKeywordData[]>);
  }, [filteredKeywordData]);

  // Get current tab data - Updated to handle consolidated Ideas & Suggestions
  const getCurrentTabData = () => {
    const currentTabConfig = TAB_CONFIG.find(tab => tab.id === activeTab);
    if (!currentTabConfig) return [];
    
    // Handle consolidated Ideas & Suggestions tab
    if (currentTabConfig.dataTypes) {
      return currentTabConfig.dataTypes.reduce((acc, dataType) => {
        return acc.concat(groupedData[dataType] || []);
      }, [] as WorkspaceKeywordData[]);
    }
    
    // Handle regular single dataType tabs - filter out empty PAA content
    const tabData = groupedData[currentTabConfig.dataType] || [];
    
    // For PAA tab, filter out items with empty content
    if (currentTabConfig.dataType === 'people_also_ask') {
      return tabData.filter(item => 
        item.people_also_ask && item.people_also_ask.trim().length > 0
      );
    }
    
    return tabData;
  };

  // Get columns for the active tab with enhanced filtering and sorting
  const getColumns = (): EnhancedTableColumn[] => {
    switch (activeTab) {
      case 'serp':
        return [
          { 
            key: 'rank', 
            label: 'Rank', 
            width: '80px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'title', 
            label: 'Title', 
            width: '300px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'url', 
            label: 'URL', 
            width: '300px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'description', 
            label: 'Description', 
            width: '400px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'domain', 
            label: 'Domain', 
            width: '200px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
        ];
      case 'subtopics':
        return [
          { 
            key: 'subtopic', 
            label: 'Subtopic', 
            width: '300px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'primary_keyword', 
            label: 'Primary Keyword', 
            width: '250px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
        ];
      case 'paa':
        return [
          { 
            key: 'people_also_ask', 
            label: 'Question', 
            width: '400px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'answer', 
            label: 'Answer', 
            width: '500px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
        ];
      case 'trends':
        return [
          { 
            key: 'primary_keyword', 
            label: 'Keyword', 
            width: '250px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'search_volume', 
            label: 'Volume', 
            width: '120px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'date_from', 
            label: 'From Date', 
            width: '150px',
            filterable: true,
            filterType: 'date',
            sortable: true,
            sortDataType: 'date'
          },
          { 
            key: 'date_to', 
            label: 'To Date', 
            width: '150px',
            filterable: true,
            filterType: 'date',
            sortable: true,
            sortDataType: 'date'
          },
        ];
      case 'keywords':
        return [
          { 
            key: 'keyword', 
            label: 'Keyword', 
            width: '250px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'search_volume', 
            label: 'Volume', 
            width: '120px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'intent', 
            label: 'Intent', 
            width: '120px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'competition', 
            label: 'Competition', 
            width: '140px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'difficulty', 
            label: 'Difficulty', 
            width: '120px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'cpc', 
            label: 'CPC', 
            width: '100px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
        ];
      default:
        return [
          { 
            key: 'keyword', 
            label: 'Keyword', 
            width: '250px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'search_volume', 
            label: 'Volume', 
            width: '120px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'intent', 
            label: 'Intent', 
            width: '120px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'competition', 
            label: 'Competition', 
            width: '140px',
            filterable: true,
            filterType: 'text',
            sortable: true,
            sortDataType: 'string'
          },
          { 
            key: 'difficulty', 
            label: 'Difficulty', 
            width: '120px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
          { 
            key: 'cpc', 
            label: 'CPC', 
            width: '100px',
            filterable: true,
            filterType: 'number',
            sortable: true,
            sortDataType: 'number'
          },
        ];
    }
  };

  const columns = getColumns();
  const currentData = getCurrentTabData();


  // Get available tabs (only show tabs that have data)
  const availableTabs = useMemo(() => {
    return TAB_CONFIG.filter(tab => {
      // Handle consolidated Ideas & Suggestions tab
      if (tab.dataTypes) {
        const hasData = tab.dataTypes.some(dataType => 
          groupedData[dataType] && groupedData[dataType].length > 0
        );
        return hasData;
      }
      
      // Handle regular single dataType tabs - check for meaningful content
      const hasData = groupedData[tab.dataType] && groupedData[tab.dataType].length > 0;
      
      // For PAA tab, also verify there's meaningful content (not just empty records)
      if (tab.dataType === 'people_also_ask' && hasData) {
        const meaningfulPAAData = groupedData[tab.dataType].filter(item => 
          item.people_also_ask && item.people_also_ask.trim().length > 0
        );
        return meaningfulPAAData.length > 0;
      }
      
      return hasData;
    });
  }, [groupedData]);

  // Auto-select first available tab if current tab has no data
  React.useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);

  // Handle tab changes
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
  };

  // Custom cell renderer for enhanced display
  const cellRenderer = (value: unknown, column: EnhancedTableColumn, row: WorkspaceKeywordData) => {

    // ✅ Handle rank column with special formatting
    if (column.key === 'rank' && value !== null && value !== undefined) {
      return (
        <div className="flex items-center">
          <Badge 
            variant={value <= 3 ? "default" : value <= 10 ? "secondary" : "outline"} 
            className={`
              text-xs font-medium
              ${value <= 3 ? 'bg-green-100 text-green-800 border-green-200' : ''}
              ${value > 3 && value <= 10 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : ''}
              ${value > 10 ? 'bg-gray-100 text-gray-600 border-gray-200' : ''}
            `}
          >
            #{value}
          </Badge>
        </div>
      );
    }

    // Handle URL columns
    if (column.key === 'url' && value) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 underline break-all whitespace-normal block"
        >
          {value}
        </a>
      );
    }

    // Handle description and answer columns with proper text wrapping
    if ((column.key === 'description' || column.key === 'answer') && value) {
      return (
        <div className="text-foreground break-words whitespace-normal">
          {value}
        </div>
      );
    }

    // Handle date columns for trends data
    if ((column.key === 'date_from' || column.key === 'date_to') && value) {
      return (
        <span className="text-foreground">
          {formatTrendsDate(value)}
        </span>
      );
    }

    // Default rendering with proper text wrapping
    return (
      <div className="text-foreground break-words whitespace-normal">
        {value || '-'}
      </div>
    );
  };

  // Cache-busting timestamp
  const componentTimestamp = Date.now();

  return (
    <div className="w-full">
      
      {/* Notion-style Tab Navigation */}
      <div className="border-b border-border/30 mb-6">
        <div className="flex space-x-6">
          {availableTabs.map((tab) => {
            const Icon = tab.icon;
            // Handle count for consolidated Keywords tab
            const count = tab.dataTypes 
              ? tab.dataTypes.reduce((total, dataType) => 
                  total + (groupedData[dataType]?.length || 0), 0)
              : groupedData[tab.dataType]?.length || 0;
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center space-x-2 px-1 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-foreground border-foreground'
                    : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {count > 0 && (
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>


      {/* Content Area */}
      <div>
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div className="text-muted-foreground text-sm">Loading workspace data...</div>
              <div className="text-muted-foreground text-xs">
                {workspaceName ? `Processing results for workspace "${workspaceName}"...` : "This may take a moment."}
              </div>
            </div>
          </div>
        )}

        {/* No data state */}
        {!isLoading && availableTabs.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div className="text-muted-foreground text-sm">No keyword data available</div>
              <div className="text-muted-foreground text-xs">
                {searchFilter 
                  ? `No keywords found matching "${searchFilter}" in this workspace`
                  : `No keyword research data found in this workspace`
                }
              </div>
            </div>
          </div>
        )}

        {/* No data for active tab */}
        {!isLoading && availableTabs.length > 0 && currentData.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <div className="text-center space-y-3">
              <div className="text-muted-foreground text-sm">No data available</div>
              <div className="text-muted-foreground text-xs">
                {searchFilter 
                  ? `No ${TAB_CONFIG.find(t => t.id === activeTab)?.label.toLowerCase()} found matching "${searchFilter}"`
                  : `No ${TAB_CONFIG.find(t => t.id === activeTab)?.label.toLowerCase()} data available`
                }
              </div>
            </div>
          </div>
        )}

        {/* Enhanced AdvancedTable with Notion-style filtering and sorting */}
        {!isLoading && currentData.length > 0 && (
          <AdvancedTable
            data={currentData}
            columns={columns}
            title={`${TAB_CONFIG.find(t => t.id === activeTab)?.label} Keywords`}
            subtitle={workspaceName ? `Workspace: ${workspaceName}` : undefined}
            cellRenderer={cellRenderer}
            isLoading={isLoading}
            enableFiltering={true}
            enableSorting={true}
            enableGlobalSearch={true}
            enableColumnVisibility={true}
            enableExport={true}
            onExport={(data) => {
              // Convert data for export button
              const exportData = data.map(row => {
                const exportRow: Record<string, any> = {};
                columns.forEach(col => {
                  exportRow[col.key] = row[col.key];
                });
                return exportRow;
              });
              // You could trigger download here or use ExportButton component
            }}
            toolbarActions={
              <div className="flex items-center gap-2">
                {onAiAnalysis && currentData.length > 0 && (
                  <AiAnalysisButton
                    onClick={onAiAnalysis}
                    disabled={isAiAnalysisLoading || currentData.length === 0}
                    isLoading={isAiAnalysisLoading}
                    size="sm"
                    showText={false}
                  />
                )}
                <ExportButton
                  data={currentData}
                  columns={columns}
                  filename={`workspace_keywords_${activeTab}`}
                  workspaceName={workspaceName}
                  tabName={activeTab}
                />
              </div>
            }
            tableStateConfig={{
              persistenceKey: `workspace-keywords-${activeTab}`,
              persistToLocalStorage: true,
              pageSize: 20
            }}
            className="w-full"
          />
        )}
      </div>
    </div>
  );
};

export default WorkspaceKeywordTabs;